'use client';

import { cloneElement, forwardRef, isValidElement, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { DrawerRootContext } from './context/drawer-root.context';
import { drawerHeaderStyles } from './styles/drawer-header.styles';
import type { DrawerHeaderPropsInterface } from './types/drawer-header.types';

/**
 * @since 10.22.0
 */
export const DrawerHeader = forwardRef<HTMLDivElement, DrawerHeaderPropsInterface>(
  (props: DrawerHeaderPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { drawer } = useContext(DrawerRootContext);
    const id = useId();
    const styles = css(drawerHeaderStyles, cssProp);

    /*
     * When the header is a single element (typically a `Heading`), label the
     * dialog from that element so `aria-labelledby` references the heading
     * itself, reusing a consumer-provided `id` when present. Otherwise, fall
     * back to labelling from the header wrapper.
     */
    const labelElement = isValidElement<{ id?: string }>(children) ? children : null;
    const labelId = labelElement?.props.id ?? id;

    useIsomorphicLayoutEffect(() => {
      drawer.setLabelId(labelId);
      return () => drawer.setLabelId(undefined);
    }, [labelId, drawer.setLabelId]);

    return (
      <div
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Drawer.Header"
        id={labelElement ? undefined : id}
        className={cx('c11n', 'c11n_drawer__header', styles, className)}
        {...rest}
      >
        {labelElement ? cloneElement(labelElement, { id: labelId }) : children}
      </div>
    );
  },
);

DrawerHeader.displayName = 'Drawer.Header';
